import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';

const SHOPIFY_APP_URL = 'https://apps.shopify.com/dressup';

const STEPS = [
  { title: 'Install the app.', body: 'Add DressUp from the Shopify App Store. Free to install.' },
  { title: 'Sync your catalog.', body: 'Products, inventory and pricing stay in step automatically.' },
  { title: 'Get recommended.', body: 'Your items surface to shoppers whose profile matches your brand.' },
] as const;

const ShopifyMark: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 109.5 124.5" fill="currentColor" aria-hidden="true">
    <path d="M74.7 14.8s-1.4.4-3.7 1.1c-.4-1.3-1-2.8-1.8-4.4-2.6-5-6.5-7.7-11.1-7.7-.3 0-.6 0-1 .1-.1-.2-.3-.3-.4-.5-2-2.2-4.6-3.2-7.7-3.1-6 .2-12 4.5-16.8 12.2-3.4 5.4-6 12.2-6.7 17.5-6.9 2.1-11.7 3.6-11.8 3.7C11 35.4 10.8 35.6 10.7 37 10.6 38 0 113.1 0 113.1l75.7 13.1V14.6c-.4.1-.7.2-1 .2zM57.4 19.8c-4 1.2-8.4 2.6-12.7 3.9.9-4.1 2.7-8.1 5.1-10.8 1.1-1.1 2.6-2.4 4.2-3.1 1.8 3.3 2.1 7.8 1.4 10zM47.5 9.7c1.4 0 2.5.3 3.5.9-1.6.8-3.1 2.1-4.6 3.6-3.7 4-6.6 10.2-7.4 16.2l-9.9 3.1C30.9 24.8 38.6 10 47.5 9.7zm5.1 54.1c-.4-6.6-8.9-8.5-9.2-14.9-.3-5.4 3.2-10.9 10.5-11.4 3.8-.2 5.8.5 5.8.5l-1.4 8.4s-2.6-.9-5.5-.7c-4.5.3-4.6 3.1-4.4 3.9.5 7.4 9.3 9 9.7 15.8.3 8.4-5.3 14.2-13.9 14.7-6.3.4-9.6-1.7-9.6-1.7l1.8-8.4s3.4 2.1 7.7 1.9c2.9-.1 4.8-1.7 4.5-4.1zm6.3-54.6c1.5-.1 3 .3 4.2 1.1-.2 0-.4.1-.6.2-3 1.5-6 4.1-8.1 8.7.5-3.1 1.9-5.7 3.3-7.6.3-.5.8-1.1 1.2-2.4zM78 126.2l31.5-7.9S96.8 20.3 96.7 19.6c-.1-.7-.7-1.1-1.2-1.2-.5 0-9.2-.2-9.2-.2s-5.3-5.2-7.3-7.1v115.1z" />
  </svg>
);

const StoresSection: React.FC = () => {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const step = STEPS[active];

  return (
    <section id="stores-section" className="screen scroll-mt-16 items-stretch">
      <div className="shell flex flex-col justify-between py-8 lg:py-16">
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="rule pt-6 lg:pt-12"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
          For Shopify stores
        </p>

        <h2 className="display mt-4 max-w-[18ch] text-[clamp(1.75rem,7vw,3.5rem)] font-medium text-ink sm:mt-5">
          Put your catalog in front of the right shopper.
        </h2>

        <p className="mt-4 max-w-[52ch] text-[14px] leading-relaxed text-muted sm:mt-6 sm:text-[15px]">
          Install the free Shopify app and your products get matched to shoppers by style, skin
          tone and size instead of competing for the same generic feed.
        </p>
      </motion.div>

      {/* Snap row on small screens, hairline columns from md up. */}
      {/* Desktop: the three steps read at once. */}
      <div className="mt-8 hidden md:grid md:grid-cols-3 md:gap-10">
        {STEPS.map((item, i) => (
          <motion.div
            key={item.title}
            initial={reduce ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className={`border-t pt-5 ${i === 0 ? 'border-accent' : 'border-line'}`}
          >
            <h3 className="text-[17px] font-semibold text-ink">{item.title}</h3>
            <p className="mt-2 max-w-[34ch] text-[14px] leading-relaxed text-muted">{item.body}</p>
          </motion.div>
        ))}
      </div>

      {/* Mobile: one step at a time, stepped through rather than scrolled. */}
      <div className="mt-6 md:hidden">
        <div
          aria-live="polite"
          className="min-h-[108px] border-t border-accent pt-4"
        >
          <p className="text-[12px] font-semibold text-muted">
            Step {active + 1} of {STEPS.length}
          </p>
          <h3 className="mt-2 text-[17px] font-semibold text-ink">{step.title}</h3>
          <p className="mt-2 text-[14px] leading-relaxed text-muted">{step.body}</p>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center">
            {STEPS.map((item, i) => (
              <button
                key={item.title}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Show step ${i + 1}: ${item.title}`}
                aria-current={i === active}
                className="flex h-11 w-8 items-center justify-center"
              >
                <span
                  className={`h-1.5 w-1.5 transition-colors ${
                    i === active ? 'bg-accent' : 'bg-line'
                  }`}
                />
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setActive((i) => Math.max(0, i - 1))}
              disabled={active === 0}
              aria-label="Previous step"
              className="flex h-11 w-11 items-center justify-center border border-line text-ink transition-colors hover:border-ink disabled:opacity-35"
            >
              <ArrowLeft strokeWidth={1.5} className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setActive((i) => Math.min(STEPS.length - 1, i + 1))}
              disabled={active === STEPS.length - 1}
              aria-label="Next step"
              className="flex h-11 w-11 items-center justify-center border border-line text-ink transition-colors hover:border-ink disabled:opacity-35"
            >
              <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col items-start gap-4 border border-line px-5 py-5 sm:mt-8 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 sm:py-6 md:px-10">
        <div className="flex items-center gap-3 sm:gap-4">
          <ShopifyMark className="h-7 w-auto shrink-0 text-ink sm:h-8" />
          <p className="max-w-[36ch] text-[14px] text-muted sm:text-[15px]">
            Free to install, with product sync included.
          </p>
        </div>

        <a
          href={SHOPIFY_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary group w-full sm:w-auto"
        >
          Install on Shopify
          <ArrowUpRight
            strokeWidth={1.5}
            className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          />
        </a>
      </div>
      </div>
    </section>
  );
};

export default StoresSection;
